import { useEffect, useState } from "react";
import { API_URL, HOST } from "@/config";
import { getTenant } from "@/helpers/tenant";
import axios, { AxiosError } from "axios";
import { RelativeComponentLoader } from "@/components/loader";
import * as Sentry from "@sentry/react";
import { toast, Typography } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";

interface OktaAuthResponse {
  status: string;
  urlWithQueryParams: string;
  pkceCodeVerifier: null;
}

/**
 * Generates the callback URL for Okta authentication
 */
function getCallbackUrl(tenant: string, host: string) {
  return encodeURI(`https://${tenant}.${host}/login/callback/okta`);
}

/**
 * Generates a state object for the OAuth flow
 */
function generateOAuthState(tenant: string) {
  // Generate random state for OAuth
  const stateValue = btoa(JSON.stringify({ state: crypto.randomUUID() }));

  // Create state object to store in session storage
  return {
    stateForAuthProvider: stateValue,
    thirdPartyId: "okta",
    tenantId: tenant,
    expiresAt: Date.now() + 3600000, // 1 hour expiry
    redirectURIOnProviderDashboard: `https://${tenant}.${HOST}/login/callback/okta`,
    pkceCodeVerifier: null,
    rid: "thirdparty",
    stateValue, // Return the stateValue separately for URL construction
  };
}

const OktaLogin = () => {
  const [loading, setLoading] = useState(true);
  const tenant = getTenant();

  useEffect(() => {
    const redirectToOkta = async () => {
      try {
        // Construct the URL for getting Okta auth URL
        const authUrl = `${API_URL}/superauth/${tenant}/authorisationurl?thirdPartyId=okta&redirectURIOnProviderDashboard=${getCallbackUrl(tenant, HOST!)}`;

        // Make the API call
        const response = await axios.get<OktaAuthResponse>(authUrl);
        const data = response.data || response;

        if (data.status === "OK") {
          // Generate state and prepare for redirect
          const state = generateOAuthState(tenant);

          // Save state to session storage
          sessionStorage.setItem("supertokens-oauth-state-2", JSON.stringify(state));

          // Redirect to Okta with state parameter added
          const redirectUrl = `${data.urlWithQueryParams}&state=${state.stateValue}`;
          window.location.href = redirectUrl;
          return;
        }

        console.error("Failed to get Okta authorization URL", response);
        setLoading(false);

        // Log to Sentry with detailed information
        Sentry.withScope(scope => {
          scope.setExtra("responseData", data);
          scope.setExtra("tenant", tenant);
          scope.setLevel("error");
          Sentry.captureMessage("Okta authorization URL request failed");
        });

        // Show user-friendly toast
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description:
            "Failed to connect to Okta. Please try again or contact support if the issue persists.",
        });
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("Error redirecting to Okta:", error);
        setLoading(false);

        // Log to Sentry with detailed error information
        Sentry.withScope(scope => {
          scope.setExtra("error", {
            message: axiosError.message,
            status: axiosError.response?.status,
            data: axiosError.response?.data,
            tenant,
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });

        // Show user-friendly toast with more specific error if available
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description:
            axiosError.response?.status === 404
              ? "Okta configuration not found. Please check your tenant settings."
              : "Failed to connect to Okta. Please try again or contact support if the issue persists.",
        });
      }
    };

    redirectToOkta();
  }, [tenant]);

  if (loading) {
    return (
      <Stack className="al-h-screen" align="center" justify="center">
        <RelativeComponentLoader label="Redirecting to Okta login..." />
      </Stack>
    );
  }

  return (
    <Stack className="al-h-screen" align="center" justify="center">
      <Stack direction="column" align="center" className="al-space-y-2">
        <Typography variant="page-heading">Failed to redirect to Okta</Typography>
        <Typography>Please try again or contact support.</Typography>
      </Stack>
    </Stack>
  );
};

export default OktaLogin;
